import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  CustomerMenu,
  MenuParseResult,
  IngredientMapResult,
  RecipeToOrderResult,
  EventPlanResult,
  SeasonalRecommendations,
  SubstituteResult,
} from '@/types/menuPlanning';
import { fetchOne, api } from './helpers';

// ---------------------------------------------------------------------------
// Menu CRUD
// ---------------------------------------------------------------------------

export function useMenus(customerId?: number) {
  return useQuery({
    queryKey: ['menus', customerId],
    queryFn: () =>
      fetchOne<{ items: CustomerMenu[] }>('/menu-planning/menus', {
        customer_id: customerId,
      }),
    enabled: !!customerId,
  });
}

export function useMenu(menuId: number) {
  return useQuery({
    queryKey: ['menus', menuId],
    queryFn: () => fetchOne<CustomerMenu>(`/menu-planning/menus/${menuId}`),
    enabled: menuId > 0,
  });
}

export function useCreateMenu() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      name: string;
      cuisine_type?: string;
      cover_count?: number;
      services_per_week?: number;
      customer_id?: number;
    }) => api.post('/menu-planning/menus', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['menus'] }),
  });
}

export function useAddDish(menuId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      name: string;
      category?: string;
      portions?: number;
      recipe_text?: string;
      ingredients?: { name: string; quantity: number; unit: string }[];
    }) => api.post(`/menu-planning/menus/${menuId}/dishes`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['menus', menuId] }),
  });
}

export function useParseMenuText(menuId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { text: string }) =>
      api.post<MenuParseResult>(`/menu-planning/menus/${menuId}/parse`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['menus', menuId] }),
  });
}

export function useMapIngredients(menuId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<IngredientMapResult>(
        `/menu-planning/menus/${menuId}/map-ingredients`,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['menus', menuId] }),
  });
}

export function useCalculateQuantities(menuId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post(`/menu-planning/menus/${menuId}/calculate`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['menus', menuId] }),
  });
}

export function useGenerateMenuOrder(menuId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post(`/menu-planning/menus/${menuId}/generate-order`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['menus', menuId] });
      qc.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

// ---------------------------------------------------------------------------
// AI Discovery
// ---------------------------------------------------------------------------

export function useRecipeToOrder() {
  return useMutation({
    mutationFn: (data: { recipe_text: string; servings?: number }) =>
      api.post<RecipeToOrderResult>(
        '/ai-customer/discovery/recipe-to-order',
        data,
      ),
  });
}

export function useAIEventPlanner() {
  return useMutation({
    mutationFn: (data: {
      event_type: string;
      guest_count: number;
      budget?: number;
      dietary_restrictions?: string[];
      preferences?: string;
    }) =>
      api.post<EventPlanResult>(
        '/ai-customer/discovery/event-planner',
        data,
      ),
  });
}

export function useAIMenuPlan() {
  return useMutation({
    mutationFn: (data: {
      cuisine_type?: string;
      cover_count?: number;
      meal_type?: string;
      preferences?: string;
    }) =>
      api.post('/ai-customer/discovery/menu-plan', data),
  });
}

export function useSeasonalRecs(customerId?: number) {
  return useQuery({
    queryKey: ['seasonal-recs', customerId],
    queryFn: () =>
      fetchOne<SeasonalRecommendations>(
        `/ai-customer/discovery/seasonal/${customerId}`,
      ),
    enabled: !!customerId,
  });
}

export function useSubstitutes(productId: number, reason?: string) {
  return useQuery({
    queryKey: ['substitutes', productId, reason],
    queryFn: () =>
      fetchOne<SubstituteResult>(
        `/ai-customer/discovery/substitute/${productId}`,
        reason ? { reason } : undefined,
      ),
    enabled: productId > 0,
  });
}
